import React, { useState, useEffect, useRef, useCallback } from "react";
import { getBackendBaseUrl } from "../../utils/apiUrl";
import {
  FiSend,
  FiMic,
  FiVideo,
  FiMinimize2,
  FiX,
  FiChevronUp,
  FiMail,
  FiCheckCircle,
  FiAlertTriangle,
  FiZap
} from "react-icons/fi";
import VideoSession from "./VideoSession";
import "./ChatBotWidget.css";

function isDarkColor(color) {
  if (!color) return false;
  const hex = color.replace('#', '');
  if (hex.length < 6) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

function normalizeText(t) {
  return t ? t.toLowerCase().replace(/[^a-z0-9]/g, "").trim() : "";
}

function parseRawUrls(text, primaryColor) {
  if (typeof text !== "string") return text;
  const urlRegex = /(https?:\/\/[^\s()]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <a
          key={`raw-link-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: primaryColor, textDecoration: "underline", fontWeight: "600", wordBreak: "break-all" }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function parseLinks(text, primaryColor) {
  if (typeof text !== "string") return text;
  const mdRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mdRegex.exec(text)) !== null) {
    const textBefore = text.slice(lastIndex, match.index);
    if (textBefore) {
      const parsedBefore = parseRawUrls(textBefore, primaryColor);
      if (Array.isArray(parsedBefore)) {
        parts.push(...parsedBefore);
      } else {
        parts.push(parsedBefore);
      }
    }
    const anchor = match[1];
    const url = match[2];
    parts.push(
      <a
        key={`link-${match.index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: primaryColor, textDecoration: "underline", fontWeight: "600", wordBreak: "break-all" }}
      >
        {anchor}
      </a>
    );
    lastIndex = mdRegex.lastIndex;
  }

  const textRemaining = text.slice(lastIndex);
  if (textRemaining) {
    const parsedRemaining = parseRawUrls(textRemaining, primaryColor);
    if (Array.isArray(parsedRemaining)) {
      parts.push(...parsedRemaining);
    } else {
      parts.push(parsedRemaining);
    }
  }

  return parts.length === 0 ? text : (parts.length === 1 ? parts[0] : parts);
}

function renderMessageContent(text, primaryColor) {
  if (!text) return "";
  const cleanedText = text.replace(/^(#{1,6})\s+/gm, "").replace(/###/g, "");
  const lines = cleanedText.split("\n");
  return lines.map((line, lineIndex) => {
    const trimmed = line.trim();
    const isBullet = trimmed.startsWith("* ") || trimmed.startsWith("- ");
    let content = isBullet ? trimmed.substring(2).trim() : line;

    // Parse **bold** tags
    const parts = content.split(/\*\*([\s\S]*?)\*\*/g);
    const parsedLine = parts.map((part, partIndex) => {
      if (partIndex % 2 === 1) {
        return <strong key={partIndex} style={{ fontWeight: "700" }}>{parseLinks(part, primaryColor)}</strong>;
      }
      return parseLinks(part, primaryColor);
    });

    if (isBullet) {
      return (
        <div key={lineIndex} style={{ display: "flex", alignItems: "flex-start", gap: "8px", margin: "6px 0 6px 12px", lineHeight: "1.4" }}>
          <span style={{ color: primaryColor, marginTop: "6.5px", fontSize: "0.55rem", flexShrink: 0 }}>●</span>
          <span style={{ flex: 1 }}>{parsedLine}</span>
        </div>
      );
    }

    return (
      <div key={lineIndex} style={{ minHeight: trimmed === "" ? "12px" : "1.4em", marginBottom: lineIndex < lines.length - 1 ? "4px" : "0" }}>
        {parsedLine}
      </div>
    );
  });
}

export default function ChatWidget({
  agentId = "default",
  primaryColor = "#65B30D",
  agentName = "Ava",
  customGreeting = "Hello! I am Ava, your video assistant. How can I help you today?",
  systemPrompt = "You are a helpful, professional, and friendly AI assistant.",
  conversationalContext = "",
  logo = "💬",
  position = "right",
  replicaId = "r291e545fd67",
  embeddedMode = false,
  uploadedDocs = [],
  documentIds = [],
  videoHeight = 200,
  offsetX = 14,
  offsetY = 14,
  bodyBgColor = "#FFFFFF",
  headerBgColor = "#1E293B",
  headerTextColor = "#FFFFFF",
  inputBgColor = "#F8FAFC",
  textColor = "#1E293B",
  minimizedText = "",
  minimizedBgColor = "",
  minimizedTextColor = "",
  widgetHeight = 560,
  widgetWidth = 400,
  verticalAlign = "bottom",
  thumbnailVideoUrl = "",
  voiceButtonText = "Talk with Avatar",
  logoBgColor = "#6366F1",
  previewMode = false,
  applyGreenscreen = false,
  avatarBackground = "",
  avatarBackgroundSize,
  avatarZoom,
  avatarTranslateX,
  avatarTranslateY,
  showAvatarPreview,
  composingText = "is composing response...",
  connectingText = "Launching video connection replica...",
  placeholderText = "Type a message...",
  joiningText = "Joining Voice Session..."
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loadingText, setLoadingText] = useState(false);
  const [voiceSessionActive, setVoiceSessionActive] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [conversationData, setConversationData] = useState({ url: "", id: "" });
  const sendVoiceMessageRef = useRef(null);
  const previewVideoRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [miniWidth, setMiniWidth] = useState(160);
  const [miniHeight, setMiniHeight] = useState(120);
  const [miniRight, setMiniRight] = useState(offsetX);
  const [miniBottom, setMiniBottom] = useState(offsetY);
  const [isAvatarCentered, setIsAvatarCentered] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadReason, setLeadReason] = useState("");
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const [parentPathname, setParentPathname] = useState("");
  const [hasTypedGreeting, setHasTypedGreeting] = useState(false);
  const [chromaKeyFailed, setChromaKeyFailed] = useState(false);
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  const [previewMounted, setPreviewMounted] = useState(0);
  const [textConversationId, setTextConversationId] = useState(null);
  const [visitorId] = useState(() => {
    let vid = localStorage.getItem("salesense_visitor_id");
    if (!vid) {
      vid = "visitor-" + Math.random().toString(36).substring(2, 11);
      localStorage.setItem("salesense_visitor_id", vid);
    }
    return vid;
  });

  const setPreviewVideoRef = useCallback((node) => {
    if (previewVideoRef.current === node) return;
    previewVideoRef.current = node;
    if (node) {
      node.play().catch(() => { });
      setPreviewMounted(prev => prev + 1);
    }
  }, []);

  const setPreviewCanvasRef = useCallback((node) => {
    if (previewCanvasRef.current === node) return;
    previewCanvasRef.current = node;
    if (node) {
      setPreviewMounted(prev => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (voiceSessionActive) {
      setHasConnectedOnce(true);
    }
  }, [voiceSessionActive]);

  useEffect(() => {
    if (window.parent !== window) {
      const updatePathname = () => {
        try {
          setParentPathname(window.parent.location.pathname);
        } catch (e) {
          // Cross-origin fallback
          setParentPathname("public-external");
        }
      };

      updatePathname();
      const interval = setInterval(updatePathname, 500);
      return () => clearInterval(interval);
    }
  }, []);



  const isProtectedRoute = (path) => {
    if (!path) return false;
    const cleanPath = path.split('?')[0].toLowerCase();

    // Explicit public routes
    const publicPaths = [
      "/new-landing",
      "/newlanding",
      "/ai-agents",
      "/platform",
      "/sales-operations",
      "/solutions",
      "/pricing",
      "/contact",
      "/demo",
      "/login",
      "/forgotpassword",
      "/reset-password",
      "/otp",
      "/dy"
    ];

    if (publicPaths.some(p => cleanPath === p || cleanPath.startsWith(p + "/"))) {
      return false;
    }

    if (cleanPath === "/" || cleanPath === "") {
      return true;
    }

    const protectedBases = [
      "/dashboard",
      "/chatbot-customizer",
      "/api-keys",
      "/user-management",
      "/avatar-selection",
      "/avatar-playground",
      "/subscriptions",
      "/dealership",
      "/pitch-dealership",
      "/pitch-aimee",
      "/testboard",
      "/products",
      "/profile",
      "/step",
      "/deals-table",
      "/f&i-service",
      "/createuser",
      "/deals",
      "/cars",
      "/customer",
      "/board",
      "/pitch-aimee-board"
    ];

    return protectedBases.some(p => cleanPath === p || cleanPath.startsWith(p + "/"));
  };

  const queryParams = new URLSearchParams(window.location.search);
  const isFrame = window.parent !== window;
  const hasAuthToken = !!localStorage.getItem("authToken");
  const isWidgetMode = queryParams.get("widget") === "true" || isFrame;
  const shouldHide = isWidgetMode && hasAuthToken && isProtectedRoute(parentPathname);
  const baseUrl = getBackendBaseUrl() + "/chatbot";

  const urlMinText = queryParams.get("minimizedText");
  const urlMinBg = queryParams.get("minimizedBgColor");
  const urlMinTextCol = queryParams.get("minimizedTextColor");
  const urlThumbnailVideoUrl = queryParams.get("thumbnailVideoUrl");
  const urlVoiceButtonText = queryParams.get("voiceButtonText");
  const urlLogoBgColor = queryParams.get("logoBgColor");
  const urlComposingText = queryParams.get("composingText");
  const urlConnectingText = queryParams.get("connectingText");
  const urlPlaceholderText = queryParams.get("placeholderText");
  const urlJoiningText = queryParams.get("joiningText");

  const resolvedMinText = minimizedText || urlMinText || `Chat with ${agentName}`;
  const resolvedComposingText = composingText || urlComposingText || "is composing response...";
  const resolvedConnectingText = connectingText || urlConnectingText || "Launching video connection replica...";
  const resolvedPlaceholderText = placeholderText || urlPlaceholderText || "Type a message...";
  const resolvedJoiningText = joiningText || urlJoiningText || "Joining Voice Session...";
  const resolvedMinBg = minimizedBgColor || urlMinBg || headerBgColor || "#1E293B";
  const resolvedMinTextCol = minimizedTextColor || urlMinTextCol || headerTextColor || "#FFFFFF";
  const resolvedThumbnailVideoUrl = urlThumbnailVideoUrl || thumbnailVideoUrl;
  const resolvedVoiceButtonText = urlVoiceButtonText || voiceButtonText || "Talk with Avatar";
  const resolvedLogoBgColor = urlLogoBgColor || logoBgColor || primaryColor || "#6366F1";

  const urlWidgetHeight = queryParams.get("widgetHeight");
  const resolvedWidgetHeight = Number(urlWidgetHeight || widgetHeight || 560);

  const urlWidgetWidth = queryParams.get("widgetWidth");
  const resolvedWidgetWidth = Number(urlWidgetWidth || widgetWidth || 400);

  const urlVerticalAlign = queryParams.get("verticalAlign");
  const resolvedVerticalAlign = verticalAlign || urlVerticalAlign || "bottom";

  const urlApplyGreenscreen = queryParams.get("applyGreenscreen") === "true";
  const urlAvatarBackground = queryParams.get("avatarBackground");
  const urlAvatarBackgroundSize = queryParams.get("avatarBackgroundSize") || "cover";
  const urlAvatarZoom = queryParams.get("avatarZoom") ? Number(queryParams.get("avatarZoom")) : 1;
  const urlSttEngine = queryParams.get("sttEngine");
  const urlLanguage = queryParams.get("language");
  const resolvedApplyGreenscreen = applyGreenscreen || urlApplyGreenscreen;
  const resolvedAvatarBackground = avatarBackground || urlAvatarBackground;
  const resolvedAvatarBackgroundSize = avatarBackgroundSize || urlAvatarBackgroundSize || "cover";
  const resolvedAvatarZoom = avatarZoom !== undefined ? Number(avatarZoom) : (urlAvatarZoom || 1);

  const urlAvatarTranslateX = queryParams.get("avatarTranslateX") ? Number(queryParams.get("avatarTranslateX")) : 0;
  const urlAvatarTranslateY = queryParams.get("avatarTranslateY") ? Number(queryParams.get("avatarTranslateY")) : 0;
  const urlShowAvatarPreview = queryParams.get("showAvatarPreview") !== null ? (queryParams.get("showAvatarPreview") === "true") : true;

  const resolvedAvatarTranslateX = avatarTranslateX !== undefined ? Number(avatarTranslateX) : urlAvatarTranslateX;
  const resolvedAvatarTranslateY = avatarTranslateY !== undefined ? Number(avatarTranslateY) : urlAvatarTranslateY;
  const resolvedShowAvatarPreview = showAvatarPreview !== undefined ? (showAvatarPreview === "true" || showAvatarPreview === true) : urlShowAvatarPreview;

  const previewZoom = previewMode ? resolvedAvatarZoom : 1;
  const previewTranslateX = previewMode ? resolvedAvatarTranslateX : 0;
  const previewTranslateY = previewMode ? resolvedAvatarTranslateY : 0;

  const videoAspectRatio = resolvedWidgetWidth / videoHeight;
  const maxAllowedHeight = typeof window !== "undefined" ? window.innerHeight * 0.92 : 900;
  let maxExpandedWidth = typeof window !== "undefined" ? Math.min(window.innerWidth * 0.96, 2000) : 1600;
  let projectedHeight = (maxExpandedWidth - resolvedWidgetWidth) / videoAspectRatio;

  if (projectedHeight > maxAllowedHeight) {
    projectedHeight = maxAllowedHeight;
    maxExpandedWidth = (projectedHeight * videoAspectRatio) + resolvedWidgetWidth;
  }

  const expandedWidth = maxExpandedWidth;
  const centeredHeight = projectedHeight;

  useEffect(() => {
    setChromaKeyFailed(false);
  }, [voiceSessionActive, replicaId]);

  useEffect(() => {
    if (voiceSessionActive || !resolvedApplyGreenscreen) return;

    const video = previewVideoRef.current;
    const canvas = previewCanvasRef.current;
    if (!video || !canvas) return;

    // Explicitly force play on mount/reopen to bypass browser autoplay restrictions
    video.play().catch((err) => {
      console.warn("Autoplay blocked or delayed:", err);
    });

    const ctx = canvas.getContext("2d");
    let active = true;

    const chromaKeyLoop = () => {
      if (!active) return;

      if (video && !video.paused && !video.ended && video.videoWidth && video.videoHeight) {
        try {
          const targetWidth = Math.min(480, video.videoWidth);
          const scale = targetWidth / video.videoWidth;
          canvas.width = targetWidth;
          canvas.height = Math.round(video.videoHeight * scale);

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = frame.data;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            // Professional difference-based green screen keying
            if (g > 90 && g > r * 1.4 && g > b * 1.4) {
              data[i + 3] = 0;
            }
          }
          ctx.putImageData(frame, 0, 0);
        } catch (e) {
          console.warn("Chroma keying error or CORS block. Falling back to normal video preview.", e);
          setChromaKeyFailed(true);
          active = false;
        }
      }

      if (active) {
        requestAnimationFrame(chromaKeyLoop);
      }
    };

    requestAnimationFrame(chromaKeyLoop);

    return () => {
      active = false;
    };
  }, [voiceSessionActive, resolvedApplyGreenscreen, replicaId, isOpen, isWidgetMinimized, previewMounted]);

  // Robust play handler to ensure video starts playing immediately on reopen/unminimize
  useEffect(() => {
    const video = previewVideoRef.current;
    if (video) {
      video.play().catch((err) => {
        console.warn("Play execution failed or blocked:", err);
      });
    }
  }, [isOpen, isWidgetMinimized, voiceSessionActive]);

  const getContainerBackgroundStyle = () => {
    if (!voiceSessionActive && resolvedShowAvatarPreview) {
      return { background: "#090D1A" };
    }
    if (!resolvedAvatarBackground) {
      return { background: "#090D1A" };
    }
    const isUrl = resolvedAvatarBackground.startsWith("http") || resolvedAvatarBackground.startsWith("/") || resolvedAvatarBackground.startsWith("data:");
    if (isUrl) {
      return {
        backgroundImage: `url(${resolvedAvatarBackground})`,
        backgroundSize: resolvedAvatarBackgroundSize,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      };
    }
    return {
      background: resolvedAvatarBackground
    };
  };

  const handleToggleAvatarCenter = () => {
    const nextVal = !isAvatarCentered;
    setIsAvatarCentered(nextVal);
    if (window.parent !== window) {
      window.parent.postMessage(nextVal ? 'aura-toggle-theater' : 'aura-toggle-maximize', '*');
    }
  };

  const dragStartRef = useRef(null);
  const hasMovedRef = useRef(false);
  const resizeStartRef = useRef(null);

  const handlePointerDown = (e) => {
    if (e.target.closest(".resize-handle") || e.target.closest("button") || e.target.closest("a")) {
      return;
    }
    e.preventDefault();
    hasMovedRef.current = false;
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialRight: miniRight,
      initialBottom: miniBottom
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragStartRef.current) return;
    e.preventDefault();
    const deltaX = e.clientX - dragStartRef.current.startX;
    const deltaY = e.clientY - dragStartRef.current.startY;

    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      hasMovedRef.current = true;
    }

    const directionFactorX = position === "right" ? -1 : 1;
    const directionFactorY = -1;

    const newRight = dragStartRef.current.initialRight + (deltaX * directionFactorX);
    const newBottom = dragStartRef.current.initialBottom + (deltaY * directionFactorY);

    setMiniRight(Math.max(0, newRight));
    setMiniBottom(Math.max(0, newBottom));

    if (embeddedMode && window.parent !== window) {
      window.parent.postMessage({
        type: 'aura-miniplayer-drag',
        dx: deltaX * directionFactorX,
        dy: deltaY * directionFactorY
      }, '*');
      dragStartRef.current.startX = e.clientX;
      dragStartRef.current.startY = e.clientY;
      dragStartRef.current.initialRight = newRight;
      dragStartRef.current.initialBottom = newBottom;
    }
  };

  const handlePointerUp = (e) => {
    if (!dragStartRef.current) return;
    dragStartRef.current = null;

    if (!hasMovedRef.current && isMiniPlayer) {
      handleExpandMiniPlayer();
    }
  };

  const handleExpandMiniPlayer = () => {
    setIsMiniPlayer(false);
    if (window.parent !== window) {
      window.parent.postMessage('aura-toggle-theater', '*');
    }
  };

  const handleResizePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialWidth: miniWidth,
      initialHeight: miniHeight
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizePointerMove = (e) => {
    if (!resizeStartRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const deltaX = e.clientX - resizeStartRef.current.startX;

    const newWidth = resizeStartRef.current.initialWidth - deltaX;
    const clampedWidth = Math.max(120, Math.min(320, newWidth));
    const clampedHeight = Math.round(clampedWidth * 0.75);

    setMiniWidth(clampedWidth);
    setMiniHeight(clampedHeight);

    if (embeddedMode && window.parent !== window) {
      window.parent.postMessage({
        type: 'aura-miniplayer-resize',
        width: clampedWidth,
        height: clampedHeight
      }, '*');
    }
  };

  const handleResizePointerUp = (e) => {
    if (!resizeStartRef.current) return;
    resizeStartRef.current = null;
  };

  const handleVoiceMessageSenderReady = useCallback((fn) => {
    sendVoiceMessageRef.current = fn;
  }, []);

  const messagesEndRef = useRef(null);

  // Initialize greeting with typing effect
  useEffect(() => {
    if (!isOpen || isWidgetMinimized) {
      setHasTypedGreeting(false);
      return;
    }
    if (hasTypedGreeting) return;

    setMessages([
      {
        id: "greeting",
        sender: "ai",
        text: "",
        final: false
      }
    ]);

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < customGreeting.length) {
        setMessages((prev) => {
          return prev.map((m) => {
            if (m.id === "greeting") {
              return {
                ...m,
                text: customGreeting.slice(0, currentIndex + 1)
              };
            }
            return m;
          });
        });
        currentIndex++;
      } else {
        setMessages((prev) => {
          return prev.map((m) => {
            if (m.id === "greeting") {
              return {
                ...m,
                final: true
              };
            }
            return m;
          });
        });
        setHasTypedGreeting(true);
        clearInterval(interval);
      }
    }, 15); // Smooth and swift typing animation (15ms per character)

    return () => clearInterval(interval);
  }, [customGreeting, hasTypedGreeting, isOpen, isWidgetMinimized]);

  // Sync state with parent window for SDK sizing
  useEffect(() => {
    if (window.parent !== window) {
      if (isWidgetMinimized) {
        window.parent.postMessage('aura-toggle-minimize', '*');
      } else if (!isOpen) {
        window.parent.postMessage('aura-toggle-bubble', '*');
      } else {
        window.parent.postMessage('aura-toggle-maximize', '*');
      }
    }
  }, [isOpen, isWidgetMinimized]);

  // Make body background transparent when loaded as a widget in iframe
  useEffect(() => {
    const isWidget = queryParams.get("widget") === "true" || isFrame;
    if (isWidget) {
      document.documentElement.style.setProperty("background", "transparent", "important");
      document.body.style.setProperty("background", "transparent", "important");
      document.documentElement.style.setProperty("background-color", "transparent", "important");
      document.body.style.setProperty("background-color", "transparent", "important");
      document.body.style.setProperty("zoom", "1", "important");
    }
  }, [isFrame]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loadingText]);

  // Style and position outer iframe container on the parent page (same origin)
  useEffect(() => {
    if (window.parent !== window) {
      try {
        const rootContainer = window.parent.document.getElementById('aura-chatbot-root');
        if (rootContainer) {
          if (shouldHide) {
            rootContainer.style.setProperty('display', 'none', 'important');
            return;
          } else {
            rootContainer.style.removeProperty('display');
          }

          // Reposition to float beautifully in bottom-right corner with absolute priority
          rootContainer.style.setProperty('position', 'fixed', 'important');
          rootContainer.style.setProperty('z-index', '999999', 'important');
          rootContainer.style.setProperty('border', 'none', 'important');
          rootContainer.style.setProperty('background', 'transparent', 'important');
          rootContainer.style.setProperty('transition', 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)', 'important');

          const hOffset = `${offsetX}px`;
          const vOffset = `${offsetY}px`;

          if (isWidgetMinimized) {
            rootContainer.style.setProperty('width', '180px', 'important');
            rootContainer.style.setProperty('height', '60px', 'important');
          } else if (!isOpen) {
            rootContainer.style.setProperty('width', '56px', 'important');
            rootContainer.style.setProperty('height', '56px', 'important');
          } else if (voiceSessionActive && isAvatarCentered) {
            rootContainer.style.setProperty('width', `min(${resolvedWidgetWidth * 2.5}px, 98vw)`, 'important');
            rootContainer.style.setProperty('height', `min(${resolvedWidgetHeight + 120}px, 90vh)`, 'important');
          } else {
            rootContainer.style.setProperty('width', `min(${resolvedWidgetWidth}px, 90vw)`, 'important');
            rootContainer.style.setProperty('height', `min(${voiceSessionActive ? resolvedWidgetHeight + 120 : resolvedWidgetHeight}px, 90vh)`, 'important');
          }

          const iframe = rootContainer.querySelector('iframe');
          if (iframe) {
            iframe.style.setProperty('width', '100%', 'important');
            iframe.style.setProperty('height', '100%', 'important');
            iframe.style.setProperty('background', 'transparent', 'important');
            iframe.style.setProperty('transition', 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)', 'important');
            iframe.style.setProperty('border-radius', '16px', 'important');
            iframe.style.setProperty('box-shadow', '0 12px 40px rgba(0, 0, 0, 0.3)', 'important');
            iframe.style.setProperty('border', '1px solid rgba(255, 255, 255, 0.1)', 'important');
          }
        }
      } catch (e) {
        console.warn("Could not style parent elements due to cross-origin or other restrictions:", e);
      }
    }
  }, [isOpen, isWidgetMinimized, voiceSessionActive, isAvatarCentered, position, offsetX, offsetY, resolvedWidgetHeight, resolvedWidgetWidth, shouldHide]);

  if (shouldHide) {
    return null;
  }

  // Handle Text-to-Text sendMessage
  const handleSendText = async (textToSend) => {
    const text = (textToSend || inputText).trim();
    if (!text) return;

    // Append user message
    const userMsg = { id: Date.now() + Math.random(), sender: "user", text, final: true };
    setMessages((prev) => [...prev, userMsg]);
    setInputText("");

    if (voiceSessionActive) {
      if (sendVoiceMessageRef.current) {
        sendVoiceMessageRef.current(text);
      }
      return;
    }

    setLoadingText(true);

    try {
      const history = messages
        .filter(m => m.id !== "greeting")
        .map(m => ({
          role: m.sender === "user" ? "user" : "assistant",
          content: m.text
        }));
      history.push({ role: "user", content: text });

      const token = localStorage.getItem("authToken");
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${baseUrl}/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          messages: history,
          systemPrompt: systemPrompt,
          agentId: agentId,
          conversationId: textConversationId,
          visitorId: visitorId
        })
      });

      if (!res.ok) {
        throw new Error("Failed to get response from text engine");
      }

      const data = await res.json();
      if (data.success && data.data && data.data.answer) {
        let answerText = data.data.answer;
        if (data.data.conversationId) {
          setTextConversationId(data.data.conversationId);
        }
        console.log("🤖 [ChatWidget] Received text response from AI:", answerText);

        const leadRegex = /\[LEAD_CAPTURE:\s*(\{[\s\S]*?\})\s*\]/;
        console.log("🔍 [ChatWidget] Scanning for lead capture tag matching:", leadRegex);
        const match = answerText.match(leadRegex);
        if (match) {
          console.log("✅ [ChatWidget] LEAD_CAPTURE tag found in message text:", match[0]);
          try {
            const leadData = JSON.parse(match[1]);
            console.log("⚙️ [ChatWidget] Parsed lead data JSON successfully:", leadData);

            // Clean the tag out of the message
            answerText = answerText.replace(leadRegex, "").trim();
            console.log("🧹 [ChatWidget] Cleaned message text (tag removed):", answerText);

            // Post to backend leads API
            const payload = {
              name: leadData.name,
              email: leadData.email,
              phone: leadData.phone,
              reason: leadData.reason,
              agentId: agentId || agentName || "Ava"
            };
            console.log("📦 [ChatWidget] POSTing lead payload to backend:", `${baseUrl}/leads`, payload);

            fetch(`${baseUrl}/leads`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload)
            }).then(response => {
              if (response.ok) {
                console.log("🎉 [ChatWidget] Successfully saved lead in MongoDB from chat tag:", leadData);
              } else {
                console.error("❌ [ChatWidget] Failed to save lead, server status:", response.status, response.statusText);
              }
            }).catch(e => {
              console.error("❌ [ChatWidget] Network error saving lead:", e);
            });
          } catch (jsonErr) {
            console.error("❌ [ChatWidget] Failed to parse lead capture tag JSON:", jsonErr, "Match source:", match[1]);
          }
        } else {
          console.log("ℹ️ [ChatWidget] No lead capture tag found in this response.");
        }

        const aiResponseId = Date.now() + Math.random();
        setMessages((prev) => [
          ...prev,
          { id: aiResponseId, sender: "ai", text: answerText, final: true }
        ]); // Smooth 15ms per character typing animation
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + Math.random(), sender: "ai", text: "Sorry, I encountered an issue. Let me try again.", final: true }
      ]);
    } finally {
      setLoadingText(false);
    }
  };

  // Launch Voice Mode
  const handleStartVoice = async () => {
    setVoiceConnecting(true);
    setVoiceError("");
    try {
      let docIds = [];
      if (documentIds && documentIds.length > 0) {
        docIds = documentIds;
      } else if (uploadedDocs && uploadedDocs.length > 0) {
        docIds = uploadedDocs.map(d => typeof d === 'string' ? d : (d.id || d.document_id)).filter(Boolean);
      }

      // Format the previous message history as context to pass to the voice session
      const chatHistoryText = messages
        .filter(m => m.id !== "greeting" && m.text)
        .map(m => `${m.sender === "user" ? "User" : "Assistant"}: ${m.text}`)
        .join("\n");

      const dynamicContext = [
        conversationalContext,
        chatHistoryText ? `Here is the conversation history with this user so far:\n${chatHistoryText}` : ""
      ].filter(Boolean).join("\n\n");

      const token = localStorage.getItem("authToken");
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${baseUrl}/conversations/start`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          replicaId,
          agentId,
          systemPrompt,
          customGreeting,
          conversationalContext: dynamicContext,
          documentIds: docIds,
          applyGreenscreen: resolvedApplyGreenscreen,
          sttEngine: urlSttEngine || undefined,
          language: urlLanguage || undefined
        })
      });

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.error || "Failed to start conversation");
      }

      const session = await res.json();
      if (session.success && session.data) {
        setConversationData({
          url: session.data.conversation_url,
          id: session.data.conversation_id
        });
        setVoiceSessionActive(true);
        setIsMiniPlayer(false);
        setIsAvatarCentered(false);
        if (window.parent !== window) {
          window.parent.postMessage('aura-toggle-maximize', '*');
        }
      }
    } catch (err) {
      console.error(err);
      setVoiceError(err.message || "Failed to start Tavus replica voice session");
    } finally {
      setVoiceConnecting(false);
    }
  };

  const handleEndVoice = async () => {
    if (!conversationData.id) {
      setVoiceSessionActive(false);
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      await fetch(`${baseUrl}/conversations/${conversationData.id}/end`, {
        method: "POST",
        headers
      });
    } catch (err) {
      console.error(err);
    } finally {
      setConversationData({ url: "", id: "" });
      setVoiceSessionActive(false);
      setIsMiniPlayer(false);
      setIsAvatarCentered(false);
      if (window.parent !== window) {
        window.parent.postMessage('aura-toggle-maximize', '*');
      }
      setTimeout(() => {
        if (previewVideoRef.current) {
          previewVideoRef.current.play().catch((err) => {
            console.warn("Autoplay preview restore blocked or delayed:", err);
          });
        }
      }, 100);
    }
  };

  const toggleOpen = () => {
    if (embeddedMode) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    // If closing the chat box, cleanly terminate any active voice session
    if (!nextOpen && voiceSessionActive) {
      handleEndVoice();
    }

    // Notify parent window for SDK sizing
    if (window.parent !== window) {
      window.parent.postMessage(nextOpen ? 'aura-toggle-maximize' : 'aura-toggle-minimize', '*');
    }
  };

  if (isWidgetMinimized) {
    return (
      <div
        onClick={() => {
          setIsWidgetMinimized(false);
          if (window.parent !== window) {
            window.parent.postMessage('aura-toggle-maximize', '*');
          }
        }}
        className="minimized-widget-bubble"
        style={
          embeddedMode
            ? {
              position: "absolute",
              bottom: "0",
              right: "0",
              width: "180px",
              height: "60px",
              background: resolvedMinBg,
              color: resolvedMinTextCol,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 14px",
              cursor: "pointer",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              fontFamily: "'Inter', sans-serif",
              pointerEvents: "auto",
              boxSizing: "border-box"
            }
            : {
              position: previewMode ? "absolute" : "fixed",
              bottom: resolvedVerticalAlign === "top" ? "auto" : `${offsetY}px`,
              top: resolvedVerticalAlign === "top" ? `${offsetY}px` : "auto",
              right: position === "right" ? `${offsetX}px` : "auto",
              left: position === "left" ? `${offsetX}px` : "auto",
              width: "180px",
              height: "60px",
              background: resolvedMinBg,
              color: resolvedMinTextCol,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 14px",
              cursor: "pointer",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              fontFamily: "'Inter', sans-serif",
              zIndex: 999999,
              pointerEvents: "auto",
              boxSizing: "border-box"
            }
        }
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
          <span style={{ fontSize: "1.2rem", flexShrink: 0, display: "flex", alignItems: "center" }}>
            {logo && logo.startsWith("http") ? (
              <img src={logo} alt="Logo" style={{ width: "24px", height: "24px", borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              logo
            )}
          </span>
          <span style={{ fontWeight: "700", fontSize: "0.8rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{resolvedMinText}</span>
        </div>
        <FiChevronUp size={16} style={{ opacity: 0.8, flexShrink: 0 }} />
      </div>
    );
  }

  const renderVideoSession = () => (
    <VideoSession
      conversationUrl={conversationData.url}
      conversationId={conversationData.id}
      replicaName={agentName}
      onEnd={handleEndVoice}
      onVoiceMessageSenderReady={handleVoiceMessageSenderReady}
      isMiniPlayer={isMiniPlayer}
      onToggleMiniPlayer={() => {
        const nextMini = !isMiniPlayer;
        setIsMiniPlayer(nextMini);
        if (window.parent !== window) {
          window.parent.postMessage(
            nextMini
              ? 'aura-toggle-miniplayer'
              : (isAvatarCentered ? 'aura-toggle-theater' : 'aura-toggle-maximize'),
            '*'
          );
        }
      }}
      isAvatarCentered={isAvatarCentered}
      onToggleAvatarCenter={handleToggleAvatarCenter}
      onTranscriptionEvent={(speaker, text, final) => {
        let processedText = text ? text.replace(/^(#{1,6})\s+/gm, "").replace(/###/g, "") : "";
        const speakerLabel = speaker === "replica" ? "ai" : "user";

        if (speakerLabel === "ai" && final && text) {
          console.log("🗣️ [ChatWidget Voice] Received final replica audio transcript:", text);
          const leadRegex = /\[LEAD_CAPTURE:\s*(\{[\s\S]*?\})\s*\]/;
          console.log("🔍 [ChatWidget Voice] Scanning transcript for lead capture tag matching:", leadRegex);
          const match = text.match(leadRegex);
          if (match) {
            console.log("✅ [ChatWidget Voice] LEAD_CAPTURE tag found in transcript:", match[0]);
            try {
              const leadData = JSON.parse(match[1]);
              console.log("⚙️ [ChatWidget Voice] Parsed lead data JSON successfully:", leadData);
              processedText = text.replace(leadRegex, "").trim();
              console.log("🧹 [ChatWidget Voice] Cleaned transcript text (tag removed):", processedText);

              // Post to backend leads API
              const payload = {
                name: leadData.name,
                email: leadData.email,
                phone: leadData.phone,
                reason: leadData.reason,
                agentId: agentId || agentName || "Ava"
              };
              console.log("📦 [ChatWidget Voice] POSTing voice lead payload to backend:", `${baseUrl}/leads`, payload);

              fetch(`${baseUrl}/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
              }).then(response => {
                if (response.ok) {
                  console.log("🎉 [ChatWidget Voice] Successfully saved lead in MongoDB:", leadData);
                } else {
                  console.error("❌ [ChatWidget Voice] Failed to save lead, status:", response.status, response.statusText);
                }
              }).catch(e => {
                console.error("❌ [ChatWidget Voice] Network error saving voice lead:", e);
              });
            } catch (jsonErr) {
              console.error("❌ [ChatWidget Voice] Failed to parse voice lead capture tag JSON:", jsonErr, "Match source:", match[1]);
            }
          } else {
            console.log("ℹ️ [ChatWidget Voice] No lead capture tag found in replica transcript.");
          }
        }

        setMessages((prev) => {
          console.log("💬 [ChatWidget Voice 1] setMessages incoming text:", processedText, "final:", final);
          if (prev.length === 0) {
            if (processedText) {
              console.log("💬 [ChatWidget Voice 1] Empty history, appending first message");
              return [{ id: Date.now() + Math.random(), sender: speakerLabel, text: processedText, final }];
            }
            return prev;
          }

          const normIncoming = normalizeText(processedText);
          console.log("💬 [ChatWidget Voice 1] Normalized incoming text:", normIncoming);

          // 1. Prevent duplicate finalized messages from the same sender within the last 5 messages
          if (final) {
            const isDuplicate = prev.slice(-5).some(
              (m) => m.sender === speakerLabel && m.final && normalizeText(m.text) === normIncoming
            );
            if (isDuplicate) {
              console.log("💬 [ChatWidget Voice 1] Ignoring duplicate finalized message detected in last 5 messages");
              return prev;
            }
          }

          // 2. Prevent duplicate streaming messages if the immediate last finalized message from same sender already contains it
          const lastMsg = prev[prev.length - 1];
          if (!final && normIncoming && lastMsg) {
            if (lastMsg.sender === speakerLabel && lastMsg.final && normalizeText(lastMsg.text).startsWith(normIncoming)) {
              console.log("💬 [ChatWidget Voice 1] Ignoring duplicate streaming message matching immediate last final message");
              return prev;
            }
          }

          const last = prev[prev.length - 1];
          console.log("💬 [ChatWidget Voice 1] Last message in history:", last);
          if (last && last.sender === speakerLabel) {
            if (last.final) {
              if (normalizeText(last.text) === normIncoming) {
                console.log("💬 [ChatWidget Voice 1] Ignoring duplicate text matching last message");
                return prev;
              }
            } else {
              const nextText = processedText || last.text;
              console.log("💬 [ChatWidget Voice 1] Updating last non-final message to:", nextText, "final:", final);
              return [...prev.slice(0, -1), { ...last, text: nextText, final }];
            }
          }

          if (processedText) {
            console.log("💬 [ChatWidget Voice 1] Appending new message bubble");
            return [...prev, { id: Date.now() + Math.random(), sender: speakerLabel, text: processedText, final }];
          }
          return prev;
        });
      }}
    />
  );

  const headerElement = (
    <div
      key="widget-header-container"
      className="widget-header"
      style={{
        display: isMiniPlayer && voiceSessionActive ? "none" : "flex",
        gridArea: "header",
        flexShrink: 0,
        background: headerBgColor,
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        padding: "16px 20px"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: resolvedLogoBgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.1rem",
            color: "#fff"
          }}
        >
          {logo && logo.startsWith("http") ? (
            <img src={logo} alt="Logo" style={{ width: "24px", height: "24px", borderRadius: "50%", objectFit: "cover" }} />
          ) : (
            logo
          )}
        </div>
        <div>
          <div style={{ fontWeight: "600", fontSize: "0.92rem", color: headerTextColor }}>
            {agentName}
          </div>
          <div style={{ fontSize: "0.72rem", color: "rgba(148, 163, 184, 0.8)", display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10B981" }}></span>
            Online
          </div>
        </div>
      </div>
      <button
        onClick={() => {
          setIsWidgetMinimized(true);
          if (window.parent !== window) {
            window.parent.postMessage('aura-toggle-minimize', '*');
          }
        }}
        style={{
          background: "none",
          border: "none",
          color: "#94A3B8",
          cursor: "pointer",
          marginLeft: "auto",
          marginRight: "10px",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        title="Minimize"
      >
        <FiMinimize2 size={16} />
      </button>
    </div>
  );

  const leadFormElement = showLeadForm && (
    <div style={{
      position: "absolute",
      top: "65px",
      left: 0,
      right: 0,
      bottom: 0,
      background: bodyBgColor,
      zIndex: 99999,
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      animation: "fadeIn 0.2s ease"
    }}>
      <h4 style={{ color: textColor, margin: 0, fontSize: "0.95rem", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}><FiMail size={16} style={{ color: primaryColor }} /> Request Callback / Inquiry</h4>
      <p style={{ color: "#94A3B8", fontSize: "0.75rem", margin: 0 }}>Please fill out the form below. We will get back to you shortly.</p>

      {leadSubmitted ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", textAlign: "center" }}>
          <FiCheckCircle size={40} style={{ color: "#10B981" }} />
          <div style={{ fontWeight: "700", color: textColor, fontSize: "0.95rem" }}>Message Sent Successfully!</div>
          <div style={{ color: "#94A3B8", fontSize: "0.78rem" }}>Thank you for your interest. We will contact you soon.</div>
          <button
            onClick={() => {
              setLeadSubmitted(false);
              setShowLeadForm(false);
            }}
            style={{
              background: primaryColor,
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: "600",
              marginTop: "12px"
            }}
          >
            Return to Chat
          </button>
        </div>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!leadName || !leadEmail || !leadPhone || !leadReason) return;
            setSubmittingLead(true);
            try {
              const res = await fetch(`${baseUrl}/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  name: leadName,
                  email: leadEmail,
                  phone: leadPhone,
                  reason: leadReason,
                  agentId: agentId || agentName || "Ava"
                })
              });
              if (res.ok) {
                setLeadSubmitted(true);
                setLeadName("");
                setLeadEmail("");
                setLeadPhone("");
                setLeadReason("");
              } else {
                const err = await res.json();
                alert("Error: " + (err.message || "Failed to submit request"));
              }
            } catch (err) {
              console.error("Lead submission error:", err);
              alert("Error connecting to server.");
            } finally {
              setSubmittingLead(false);
            }
          }}
          style={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}
        >
          <div>
            <label style={{ display: "block", fontSize: "0.7rem", color: "#94A3B8", marginBottom: "2px", fontWeight: "600" }}>Name</label>
            <input
              type="text"
              required
              placeholder="Your Full Name"
              value={leadName}
              onChange={(e) => setLeadName(e.target.value)}
              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", background: inputBgColor, border: "1px solid rgba(0,0,0,0.08)", color: textColor, outline: "none", fontSize: "0.8rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.7rem", color: "#94A3B8", marginBottom: "2px", fontWeight: "600" }}>Email</label>
            <input
              type="email"
              required
              placeholder="name@company.com"
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", background: inputBgColor, border: "1px solid rgba(0,0,0,0.08)", color: textColor, outline: "none", fontSize: "0.8rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.7rem", color: "#94A3B8", marginBottom: "2px", fontWeight: "600" }}>Phone Number</label>
            <input
              type="tel"
              required
              placeholder="Phone number"
              value={leadPhone}
              onChange={(e) => setLeadPhone(e.target.value)}
              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", background: inputBgColor, border: "1px solid rgba(0,0,0,0.08)", color: textColor, outline: "none", fontSize: "0.8rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.7rem", color: "#94A3B8", marginBottom: "2px", fontWeight: "600" }}>Inquiry / Reason</label>
            <textarea
              required
              placeholder="What are you interested in? (e.g. Phil Long inventory pricing...)"
              value={leadReason}
              onChange={(e) => setLeadReason(e.target.value)}
              rows={2}
              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", background: inputBgColor, border: "1px solid rgba(0,0,0,0.08)", color: textColor, outline: "none", resize: "none", fontSize: "0.8rem" }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "auto" }}>
            <button
              type="button"
              onClick={() => setShowLeadForm(false)}
              style={{ flex: 1, padding: "8px", borderRadius: "6px", background: "rgba(0,0,0,0.05)", border: "none", color: textColor, cursor: "pointer", fontWeight: "600", fontSize: "0.8rem" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submittingLead}
              style={{ flex: 2, padding: "8px", borderRadius: "6px", background: primaryColor, border: "none", color: "#fff", cursor: "pointer", fontWeight: "600", fontSize: "0.8rem" }}
            >
              {submittingLead ? "Submitting..." : "Submit Inquiry"}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const videoPanelElement = (
    <div
      key="video-panel-element"
      onPointerDown={isMiniPlayer && voiceSessionActive ? handlePointerDown : undefined}
      onPointerMove={isMiniPlayer && voiceSessionActive ? handlePointerMove : undefined}
      onPointerUp={isMiniPlayer && voiceSessionActive ? handlePointerUp : undefined}
      style={
        (voiceSessionActive && isMiniPlayer)
          ? {
            position: "fixed",
            bottom: `${miniBottom}px`,
            right: position === "right" ? `${miniRight}px` : "auto",
            left: position === "left" ? `${miniRight}px` : "auto",
            height: `${miniHeight}px`,
            width: `${miniWidth}px`,
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            border: "2px solid rgba(255, 255, 255, 0.15)",
            zIndex: 99999,
            overflow: "hidden",
            cursor: "grab",
            transition: "all 0.3s ease"
          }
          : (voiceSessionActive && isAvatarCentered)
            ? {
              gridArea: "video",
              width: "100%",
              height: "100%",
              alignSelf: "stretch",
              justifySelf: "stretch",
              borderRight: bodyBgColor === "#FFFFFF" ? "1px solid #E5E7EB" : "1px solid rgba(255, 255, 255, 0.08)",
              overflow: "hidden",
              transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }
            : voiceSessionActive
              ? {
                width: "100%",
                height: `${videoHeight}px`,
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
                gridArea: "video",
                transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
              }
              : {
                width: "100%",
                height: `${videoHeight}px`,
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
                gridArea: "video",
                transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                ...getContainerBackgroundStyle()
              }
      }
    >
      {voiceSessionActive ? (
        <VideoSession
          key="active-video-session"
          conversationUrl={conversationData.url}
          conversationId={conversationData.id}
          replicaName={agentName}
          onEnd={handleEndVoice}
          onVoiceMessageSenderReady={handleVoiceMessageSenderReady}
          isMiniPlayer={isMiniPlayer}
          applyGreenscreen={resolvedApplyGreenscreen}
          avatarBackground={resolvedAvatarBackground}
          avatarBackgroundSize={resolvedAvatarBackgroundSize}
          avatarZoom={resolvedAvatarZoom}
          avatarTranslateX={resolvedAvatarTranslateX}
          avatarTranslateY={resolvedAvatarTranslateY}
          joiningText={resolvedJoiningText}
          aspectRatio={(isMiniPlayer || isAvatarCentered) ? undefined : `${resolvedWidgetWidth} / ${videoHeight}`}
          onToggleMiniPlayer={() => {
            const nextMini = !isMiniPlayer;
            setIsMiniPlayer(nextMini);
            if (window.parent !== window) {
              window.parent.postMessage(
                nextMini
                  ? 'aura-toggle-miniplayer'
                  : (isAvatarCentered ? 'aura-toggle-theater' : 'aura-toggle-maximize'),
                '*'
              );
            }
          }}
          isAvatarCentered={isAvatarCentered}
          onToggleAvatarCenter={handleToggleAvatarCenter}
          onTranscriptionEvent={(speaker, text, final) => {
            let processedText = text ? text.replace(/^(#{1,6})\s+/gm, "").replace(/###/g, "") : "";
            const speakerLabel = speaker === "replica" ? "ai" : "user";

            if (speakerLabel === "ai" && final && text) {
              console.log("🗣️ [ChatWidget Voice] Received final replica audio transcript:", text);
              const leadRegex = /\[LEAD_CAPTURE:\s*(\{[\s\S]*?\})\s*\]/;
              console.log("🔍 [ChatWidget Voice] Scanning transcript for lead capture tag matching:", leadRegex);
              const match = text.match(leadRegex);
              if (match) {
                console.log("✅ [ChatWidget Voice] LEAD_CAPTURE tag found in transcript:", match[0]);
                try {
                  const leadData = JSON.parse(match[1]);
                  console.log("⚙️ [ChatWidget Voice] Parsed lead data JSON successfully:", leadData);
                  processedText = text.replace(leadRegex, "").trim();
                  console.log("🧹 [ChatWidget Voice] Cleaned transcript text (tag removed):", processedText);

                  // Post to backend leads API
                  const payload = {
                    name: leadData.name,
                    email: leadData.email,
                    phone: leadData.phone,
                    reason: leadData.reason,
                    agentId: agentId || agentName || "Ava"
                  };
                  console.log("📦 [ChatWidget Voice] POSTing voice lead payload to backend:", `${baseUrl}/leads`, payload);

                  fetch(`${baseUrl}/leads`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                  }).then(response => {
                    if (response.ok) {
                      console.log("🎉 [ChatWidget Voice] Successfully saved lead in MongoDB:", leadData);
                    } else {
                      console.error("❌ [ChatWidget Voice] Failed to save lead, status:", response.status, response.statusText);
                    }
                  }).catch(e => {
                    console.error("❌ [ChatWidget Voice] Network error saving voice lead:", e);
                  });
                } catch (jsonErr) {
                  console.error("❌ [ChatWidget Voice] Failed to parse voice lead capture tag JSON:", jsonErr, "Match source:", match[1]);
                }
              } else {
                console.log("ℹ️ [ChatWidget Voice] No lead capture tag found in replica transcript.");
              }
            }

            setMessages((prev) => {
              console.log("💬 [ChatWidget Voice 2] setMessages incoming text:", processedText, "final:", final);
              if (prev.length === 0) {
                if (processedText) {
                  console.log("💬 [ChatWidget Voice 2] Empty history, appending first message");
                  return [{ id: Date.now() + Math.random(), sender: speakerLabel, text: processedText, final }];
                }
                return prev;
              }

              const normIncoming = normalizeText(processedText);
              console.log("💬 [ChatWidget Voice 2] Normalized incoming text:", normIncoming);

              // 1. Prevent duplicate finalized messages from the same sender within the last 5 messages
              if (final) {
                const isDuplicate = prev.slice(-5).some(
                  (m) => m.sender === speakerLabel && m.final && normalizeText(m.text) === normIncoming
                );
                if (isDuplicate) {
                  console.log("💬 [ChatWidget Voice 2] Ignoring duplicate finalized message detected in last 5 messages");
                  return prev;
                }
              }

              // 2. Prevent duplicate streaming messages if the immediate last finalized message from same sender already contains it
              const lastMsg = prev[prev.length - 1];
              if (!final && normIncoming && lastMsg) {
                if (lastMsg.sender === speakerLabel && lastMsg.final && normalizeText(lastMsg.text).startsWith(normIncoming)) {
                  console.log("💬 [ChatWidget Voice 2] Ignoring duplicate streaming message matching immediate last final message");
                  return prev;
                }
              }

              const last = prev[prev.length - 1];
              console.log("💬 [ChatWidget Voice 2] Last message in history:", last);
              if (last && last.sender === speakerLabel) {
                if (last.final) {
                  if (normalizeText(last.text) === normIncoming) {
                    console.log("💬 [ChatWidget Voice 2] Ignoring duplicate text matching last message");
                    return prev;
                  }
                } else {
                  const nextText = processedText || last.text;
                  console.log("💬 [ChatWidget Voice 2] Updating last non-final message to:", nextText, "final:", final);
                  return [...prev.slice(0, -1), { ...last, text: nextText, final }];
                }
              }

              if (processedText) {
                console.log("💬 [ChatWidget Voice 2] Appending new message bubble");
                return [...prev, { id: Date.now() + Math.random(), sender: speakerLabel, text: processedText, final }];
              }
              return prev;
            });
          }}
        />
      ) : (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
          {resolvedShowAvatarPreview && (
            <>
              <video
                ref={setPreviewVideoRef}
                key={replicaId}
                crossOrigin="anonymous"
                src={
                  resolvedThumbnailVideoUrl || (
                    replicaId === "r9211b98614f"
                      ? "https://cdn.replica.tavus.io/39470/eaf68e0d_normalized.mp4"
                      : replicaId === "r4ba1277e4fb"
                        ? "https://cdn.replica.tavus.io/39472/a961b115_normalized.mp4"
                        : "https://cdn.replica.tavus.io/39418/a3b943c5_normalized.mp4"
                  )
                }
                autoPlay
                loop
                muted
                playsInline
                onLoadedData={(e) => {
                  e.target.play().catch(() => { });
                }}
                onCanPlay={(e) => {
                  e.target.play().catch(() => { });
                }}
                style={
                  (resolvedApplyGreenscreen && !chromaKeyFailed)
                    ? {
                      position: "absolute",
                      top: 0,
                      left: "-9999px",
                      width: "1px",
                      height: "1px",
                      opacity: 0,
                      pointerEvents: "none"
                    }
                    : {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: (resolvedApplyGreenscreen && !chromaKeyFailed) ? `scale(${previewZoom}) translate(${previewTranslateX}px, ${previewTranslateY}px)` : "scale(1) translate(0px, 0px)",
                      transformOrigin: "center center",
                      transition: "transform 0.3s ease",
                      display: "block"
                    }
                }
              />
              {resolvedApplyGreenscreen && (
                <canvas
                  ref={setPreviewCanvasRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: `scale(${previewZoom}) translate(${previewTranslateX}px, ${previewTranslateY}px)`,
                    transformOrigin: "center center",
                    transition: "transform 0.3s ease",
                    display: chromaKeyFailed ? "none" : "block"
                  }}
                />
              )}
            </>
          )}
          <div
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              background: "rgba(15, 23, 42, 0.75)",
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "0.72rem",
              color: "#fff",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            <FiVideo size={12} /> Preview Demo
          </div>
          {!voiceSessionActive && !voiceConnecting && (
            <div
              style={{
                position: "absolute",
                bottom: "16px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                textAlign: "center",
              }}
            >
              <button
                onClick={handleStartVoice}
                className="voice-session-btn"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, #0c1524 130%)`,
                  color: "#fff",
                  border: "none",
                  borderRadius: "24px",
                  padding: "10px 24px",
                  fontSize: "0.85rem",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: `0 4px 15px rgba(0,0,0,0.3)`,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.25s ease",
                  whiteSpace: "nowrap"
                }}
              >
                <FiMic size={16} /> {resolvedVoiceButtonText}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const messageFeedElement = (
    <div
      className="widget-message-feed"
      style={{
        display: isMiniPlayer && voiceSessionActive ? "none" : "flex",
        gridArea: "feed",
        flex: 1,
        overflowY: "auto",
        padding: "20px",
        background: bodyBgColor,
        flexDirection: "column",
        gap: "12px"
      }}
    >
      {messages.map((msg, i) => {
        const isUser = msg.sender === "user";
        return (
          <div
            key={msg.id || i}
            style={{
              animation: "fadeInUp 0.2s ease",
              display: "flex",
              justifyContent: isUser ? "flex-end" : "flex-start",
              width: "100%",
              marginBottom: "8px"
            }}
          >
            <div
              style={{
                maxWidth: "80%",
                padding: "10px 14px",
                borderRadius: isUser ? "16px 16px 2px 16px" : "16px 16px 16px 2px",
                background: isUser
                  ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`
                  : (isDarkColor(bodyBgColor) ? "rgba(255, 255, 255, 0.08)" : "#FFFFFF"),
                color: isUser ? "#FFFFFF" : (isDarkColor(bodyBgColor) ? "#F8FAFC" : "#1E293B"),
                border: isUser ? "none" : (isDarkColor(bodyBgColor) ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)"),
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                fontSize: "0.86rem",
                lineHeight: "1.45",
                whiteSpace: "pre-wrap",
                fontFamily: "'Inter', sans-serif"
              }}
            >
              {!isUser && (
                <div
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: "750",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: primaryColor,
                    marginBottom: "4px"
                  }}
                >
                  {agentName}
                </div>
              )}
              {renderMessageContent(msg.text, primaryColor)}
            </div>
          </div>
        );
      })}

      {loadingText && (
        <div style={{ color: "#94A3B8", fontSize: "0.82rem", fontStyle: "italic", alignSelf: "flex-start", paddingLeft: "10px" }}>
          {agentName} {resolvedComposingText}
        </div>
      )}

      {voiceConnecting && (
        <div style={{ color: "#6366F1", fontSize: "0.82rem", fontStyle: "italic", textAlign: "center", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <FiZap className="animate-pulse" /> {resolvedConnectingText}
        </div>
      )}

      {voiceError && (
        <div style={{ color: "#EF4444", fontSize: "0.8rem", textAlign: "center", padding: "10px", background: "#FEF2F2", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <FiAlertTriangle /> {voiceError}
        </div>
      )}

      {/* Voice session button is placed on the avatar preview overlay instead of inside the message list */}

      <div ref={messagesEndRef} />
    </div>
  );

  const inputAreaElement = (
    <div
      style={{
        display: isMiniPlayer && voiceSessionActive ? "none" : "block",
        flexShrink: 0,
        padding: "12px 16px",
        borderTop: bodyBgColor === "#FFFFFF" ? "1px solid #E5E7EB" : "1px solid rgba(255, 255, 255, 0.08)",
        background: inputBgColor
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendText();
        }}
        style={{ display: "flex", gap: "8px", alignItems: "center" }}
      >
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={resolvedPlaceholderText}
          className="widget-input-field"
          style={{
            flex: 1,
            border: bodyBgColor === "#FFFFFF" ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            padding: "10px 18px",
            fontSize: "0.88rem",
            outline: "none",
            background: bodyBgColor === "#FFFFFF" ? "#fff" : "rgba(10, 15, 30, 0.6)",
            color: textColor,
            transition: "all 0.2s ease"
          }}
        />
        <button
          type="submit"
          className="widget-send-btn"
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
            border: "none",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: `0 4px 10px ${primaryColor}30`,
            transition: "all 0.2s ease"
          }}
        >
          <FiSend size={16} style={{ marginLeft: "2px" }} />
        </button>
      </form>
    </div>
  );

  const chatPane = (
    <div
      key="chat-pane-container"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        gridArea: "chat",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
        transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
      }}
    >
      {leadFormElement}
      {messageFeedElement}
      {inputAreaElement}
    </div>
  );

  const showVideo = voiceSessionActive || (resolvedShowAvatarPreview && !isMiniPlayer);

  return (
    <div style={{ pointerEvents: "auto", height: embeddedMode ? "100%" : "auto", display: embeddedMode ? "flex" : "block", flexDirection: embeddedMode ? "column" : undefined, "--primary": primaryColor, "--primary-glow": primaryColor + "40" }}>
      {/* Collapsed bubble */}
      {!embeddedMode && !isOpen && (
        <button
          onClick={toggleOpen}
          className="chat-bubble-btn"
          style={{
            background: resolvedLogoBgColor,
            boxShadow: `0 4px 20px ${resolvedLogoBgColor}40`,
            bottom: previewMode ? (resolvedVerticalAlign === "top" ? "auto" : `${offsetY}px`) : isFrame ? (resolvedVerticalAlign === "top" ? "auto" : "4px") : (resolvedVerticalAlign === "top" ? "auto" : offsetY + "px"),
            top: previewMode ? (resolvedVerticalAlign === "top" ? `${offsetY}px` : "auto") : isFrame ? (resolvedVerticalAlign === "top" ? "4px" : "auto") : (resolvedVerticalAlign === "top" ? offsetY + "px" : "auto"),
            right: previewMode ? (position === "right" ? `${offsetX}px` : "auto") : isFrame ? (position === "right" ? "4px" : "auto") : (position === "right" ? offsetX + "px" : "auto"),
            left: previewMode ? (position === "left" ? `${offsetX}px` : "auto") : isFrame ? (position === "left" ? "4px" : "auto") : (position === "left" ? offsetX + "px" : "auto"),
            position: previewMode ? "absolute" : "fixed"
          }}
        >
          <span style={{ fontSize: "1.3rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {logo && logo.startsWith("http") ? (
              <img src={logo} alt="Logo" style={{ width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              logo
            )}
          </span>
        </button>
      )}

      {/* Centered Backdrop Overlay for Avatar View — only in SDK iframe mode */}
      {isOpen && voiceSessionActive && isAvatarCentered && !isMiniPlayer && isFrame && (
        <div
          onClick={handleToggleAvatarCenter}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            zIndex: 999998
          }}
        />
      )}

      {/* Expanded Chat Widget */}
      {(!embeddedMode || isOpen) && (
        <div
          key="chat-widget-root"
          className={`chat-widget ${isOpen ? "visible" : "hidden"}`}
          onPointerDown={isMiniPlayer && voiceSessionActive ? handlePointerDown : undefined}
          onPointerMove={isMiniPlayer && voiceSessionActive ? handlePointerMove : undefined}
          onPointerUp={isMiniPlayer && voiceSessionActive ? handlePointerUp : undefined}
          style={
            embeddedMode
              ? (isMiniPlayer && voiceSessionActive
                ? {
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  bottom: "auto",
                  right: "auto",
                  left: "auto",
                  boxShadow: "none",
                  borderRadius: "12px",
                  border: "2px solid rgba(255, 255, 255, 0.15)",
                  display: "grid",
                  gridTemplateAreas: "'video'",
                  gridTemplateRows: "100%",
                  gridTemplateColumns: "100%",
                  background: bodyBgColor,
                  overflow: "hidden",
                  transition: "opacity 0.25s ease, box-shadow 0.25s ease"
                }
                : (voiceSessionActive && isAvatarCentered
                  ? {
                    position: "absolute",
                    bottom: `10px`,
                    right: "auto",
                    left: "10px",
                    width: `${expandedWidth}px`,
                    height: `${centeredHeight}px`,
                    maxHeight: "none",
                    borderRadius: "16px",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    display: "grid",
                    gridTemplateColumns: `1fr ${resolvedWidgetWidth}px`,
                    gridTemplateRows: "auto 1fr",
                    gridTemplateAreas: '"video header" "video chat"',
                    background: bodyBgColor,
                    overflow: "hidden",
                    transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease, box-shadow 0.4s ease",
                    zIndex: 9999
                  }
                  : {
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    maxHeight: "none",
                    flex: 1,
                    minHeight: 0,
                    bottom: "auto",
                    right: "auto",
                    left: "auto",
                    boxShadow: "none",
                    borderRadius: isFrame ? "0" : "12px",
                    border: isFrame ? "none" : "1px solid rgba(255, 255, 255, 0.08)",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gridTemplateRows: showVideo ? "auto auto 1fr" : "auto 1fr",
                    gridTemplateAreas: showVideo ? '"header" "video" "chat"' : '"header" "chat"',
                    background: bodyBgColor,
                    overflow: "hidden",
                    transition: "opacity 0.25s ease, box-shadow 0.25s ease, border-radius 0.25s ease, grid-template-columns 0.4s ease, grid-template-rows 0.4s ease"
                  }
                )
              )
              : (isMiniPlayer && voiceSessionActive
                ? {
                  position: "fixed",
                  // bottom: `${miniBottom}px`,
                  // right: position === "right" ? `${miniRight}px` : "auto",
                  // left: position === "left" ? `${miniRight}px` : "auto",
                  inset: "auto 10px 10px auto",
                  height: `${miniHeight}px`,
                  width: `${miniWidth}px`,
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                  border: "2px solid rgba(255, 255, 255, 0.15)",
                  zIndex: 999999,
                  overflow: "hidden",
                  background: "#090D1A",
                  display: "flex",
                  flexDirection: "column",
                  transition: "opacity 0.25s ease, box-shadow 0.25s ease",
                  top: "auto",
                  cursor: "grab"
                }
                : (voiceSessionActive && isAvatarCentered
                  ? {
                    position: previewMode ? "absolute" : "fixed",
                    bottom: resolvedVerticalAlign === "top" ? "auto" : (previewMode ? `${offsetY}px` : isFrame ? "0px" : `${offsetY}px`),
                    top: resolvedVerticalAlign === "top" ? (previewMode ? `${offsetY}px` : isFrame ? "0px" : `${offsetY}px`) : "auto",
                    right: position === "right" ? (previewMode ? `${offsetX}px` : isFrame ? "0px" : `${offsetX}px`) : "auto",
                    left: position === "left" ? (previewMode ? `${offsetX}px` : isFrame ? "0px" : `${offsetX}px`) : "auto",
                    width: `${expandedWidth}px`,
                    height: `${centeredHeight}px`,
                    maxHeight: "none",
                    borderRadius: "16px",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    display: "grid",
                    gridTemplateColumns: `1fr ${resolvedWidgetWidth}px`,
                    gridTemplateRows: "auto 1fr",
                    gridTemplateAreas: '"video header" "video chat"',
                    background: bodyBgColor,
                    overflow: "hidden",
                    transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1), bottom 0.4s ease, top 0.4s ease, left 0.4s ease, right 0.4s ease, box-shadow 0.4s ease",
                    zIndex: 999999
                  }
                  : {
                    position: previewMode ? "absolute" : "fixed",
                    bottom: resolvedVerticalAlign === "top" ? "auto" : (previewMode ? `${offsetY}px` : isFrame ? "0px" : `${offsetY}px`),
                    top: resolvedVerticalAlign === "top" ? (previewMode ? `${offsetY}px` : isFrame ? "0px" : `${offsetY}px`) : "auto",
                    right: position === "right" ? (previewMode ? `${offsetX}px` : isFrame ? "0px" : `${offsetX}px`) : "auto",
                    left: position === "left" ? (previewMode ? `${offsetX}px` : isFrame ? "0px" : `${offsetX}px`) : "auto",
                    height: "min(" + (voiceSessionActive ? resolvedWidgetHeight + 120 : resolvedWidgetHeight) + "px, 90vh)",
                    maxHeight: "90vh",
                    width: "min(" + resolvedWidgetWidth + "px, 90vw)",
                    borderRadius: "16px",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gridTemplateRows: showVideo ? "auto auto 1fr" : "auto 1fr",
                    gridTemplateAreas: showVideo ? '"header" "video" "chat"' : '"header" "chat"',
                    background: bodyBgColor,
                    overflow: "hidden",
                    transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s cubic-bezier(0.16, 1, 0.3, 1), bottom 0.4s ease, top 0.4s ease, left 0.4s ease, right 0.4s ease, box-shadow 0.4s ease",
                    zIndex: 999999
                  }
                )
              )
          }
        >
          {isMiniPlayer && voiceSessionActive && (
            <div
              className="resize-handle"
              onPointerDown={handleResizePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handleResizePointerUp}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "24px",
                height: "24px",
                cursor: "nwse-resize",
                zIndex: 999999,
                background: "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)",
                borderTopLeftRadius: "10px",
                pointerEvents: "auto"
              }}
            />
          )}
          {headerElement}
          {showVideo && videoPanelElement}
          {chatPane}
        </div>
      )}
    </div>
  );
}
